import { Controller } from "@hotwired/stimulus"

/**
 * Document Pages controller
 * Manages document pages with 3D page turning animations
 * Handles both PDFs (multi-page) and images (single page)
 * Also provides version comparison with interactive slider
 */
export default class extends Controller {
  static targets = [
    "page", 
    "indicator", 
    "pageCounter", 
    "currentPageNum",
    "prevButton",
    "nextButton",
    "versionSelector",
    "versionImage",
    "compareSwitch",
    "previewContainer",
    "regularView",
    "comparisonView",
    "comparisonPage",
    "compareSlider",
    "compareVersionContainer",
    "sliderHandle",
    "compareOriginal",
    "compareVersion"
  ]

  connect() {
    this.currentPage = 0
    this.totalPages = this.pageTargets.length
    this.isDragging = false
    
    // Check if this is a PDF (has navigation) or an image (no navigation)
    const hasPagination = this.hasPrevButtonTarget && this.hasNextButtonTarget
    
    // For PDFs with navigation
    if (hasPagination) {
      if (this.totalPages <= 1) {
        this.prevButtonTarget.disabled = true
        this.nextButtonTarget.disabled = true
      }
    }
    
    // Initialize slider events if we have the compare elements
    if (this.hasSliderHandleTarget) {
      // Add document-level event listeners for slider dragging
      document.addEventListener('mousemove', this.handleDrag.bind(this))
      document.addEventListener('mouseup', this.stopDrag.bind(this))
      document.addEventListener('touchmove', this.handleDrag.bind(this))
      document.addEventListener('touchend', this.stopDrag.bind(this))
      
      // Initialize all slider image positions to ensure proper sizing on page load
      this.initializeSliderImages()
    }
    
    // Update current page styling
    this.updatePageDisplay()
  }
  
  // Initialize all slider images for proper sizing on page load
  initializeSliderImages() {
    // Find all comparison sliders
    const sliders = this.element.querySelectorAll('[data-document-pages-target="compareSlider"]')
    sliders.forEach(slider => {
      const versionContainer = slider.querySelector('[data-document-pages-target="compareVersionContainer"]')
      const versionImage = versionContainer ? versionContainer.querySelector('img') : null
      const originalContainer = slider.querySelector('.document-app__compare-original')
      const originalImage = originalContainer ? originalContainer.querySelector('img') : null
      
      if (versionImage) {
        // Set initial styles for version image
        versionImage.style.position = 'absolute'
        versionImage.style.top = '0'
        versionImage.style.left = '0'
        versionImage.style.width = `${slider.offsetWidth}px`
        versionImage.style.height = '100%'
        versionImage.style.maxWidth = 'none'
        versionImage.style.objectFit = 'contain'
        versionImage.style.objectPosition = 'center'
      }
      
      if (originalImage) {
        // Set initial styles for original image
        originalImage.style.position = 'absolute'
        originalImage.style.top = '0'
        originalImage.style.left = '0'
        originalImage.style.width = '100%'
        originalImage.style.height = '100%'
        originalImage.style.objectFit = 'contain'
        originalImage.style.objectPosition = 'center'
      }
    })
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    document.removeEventListener('mousemove', this.handleDrag.bind(this))
    document.removeEventListener('mouseup', this.stopDrag.bind(this))
    document.removeEventListener('touchmove', this.handleDrag.bind(this))
    document.removeEventListener('touchend', this.stopDrag.bind(this))
  }

  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }
  
  goToPage(event) {
    const pageIndex = parseInt(event.currentTarget.dataset.page)
    if (pageIndex >= 0 && pageIndex < this.totalPages) {
      this.currentPage = pageIndex
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }

  // Toggle between standard view and comparison view
  toggleCompareMode(event) {
    const isCompareMode = event.target.checked
    
    if (isCompareMode) {
      // Show comparison view, hide regular view
      if (this.hasRegularViewTarget) this.regularViewTarget.classList.add('d-none')
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.remove('d-none')
      
      // Update comparison page visibility to show current page
      this.updateComparisonPages()
      
      // Reset slider position to 50% for consistent experience
      if (this.hasComparisonPageTarget) {
        this.comparisonPageTargets.forEach(page => {
          const sliderHandle = page.querySelector('[data-document-pages-target="sliderHandle"]')
          const versionContainer = page.querySelector('[data-document-pages-target="compareVersionContainer"]')
          
          if (sliderHandle && versionContainer) {
            sliderHandle.style.left = '50%'
            versionContainer.style.width = '50%'
          }
        })
      }
      
      // Initialize image sizes when entering comparison mode
      // Do this after a slight delay to ensure the view is rendered
      setTimeout(() => this.initializeSliderImages(), 50)
      
      // Disable zoom and reset buttons in comparison mode
      this.toggleZoomButtons(true) // true = disable
    } else {
      // Show regular view, hide comparison view
      if (this.hasRegularViewTarget) this.regularViewTarget.classList.remove('d-none')
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.add('d-none')
      
      // Re-enable zoom and reset buttons
      this.toggleZoomButtons(false) // false = enable
    }
  }
  
  // Helper method to toggle zoom buttons
  toggleZoomButtons(disable) {
    const zoomButtons = this.element.querySelectorAll('.btn[data-action^="magnifier#"]')
    zoomButtons.forEach(button => {
      button.disabled = disable
      
      if (disable) {
        // Hide buttons completely in comparison mode to avoid any borders
        button.style.visibility = 'hidden'
        button.style.opacity = '0'
        button.style.pointerEvents = 'none'
        button.setAttribute('title', 'Zoom not available in comparison mode')
      } else {
        // Reset styles
        button.style.visibility = ''
        button.style.opacity = ''
        button.style.pointerEvents = ''
        
        // Restore original titles
        if (button.getAttribute('data-action') === 'magnifier#zoomIn') {
          button.setAttribute('title', 'Zoom in')
        } else if (button.getAttribute('data-action') === 'magnifier#zoomOut') {
          button.setAttribute('title', 'Zoom out')
        } else if (button.getAttribute('data-action') === 'magnifier#resetZoom') {
          button.setAttribute('title', 'Reset zoom')
        }
      }
    })
  }
  
  // Helper method to update comparison pages
  updateComparisonPages() {
    if (this.hasComparisonPageTarget) {
      this.comparisonPageTargets.forEach((page, index) => {
        const pageIndex = parseInt(page.dataset.pageIndex)
        if (pageIndex === this.currentPage) {
          page.classList.remove('d-none')
          page.classList.add('active')
        } else {
          page.classList.add('d-none')
          page.classList.remove('active')
        }
      })
    }
  }
  
  // Select document version
  selectVersion(event) {
    const selectedVersion = event.target.value
    
    // Hide all version images first
    if (this.hasVersionImageTarget) {
      this.versionImageTargets.forEach(img => {
        img.style.display = 'none'
      })
      
      // Show images for the selected version
      this.versionImageTargets.forEach(img => {
        if (img.dataset.version === selectedVersion) {
          img.style.display = 'block'
        }
      })
    }
  }
  
  // Slider comparison functionality
  startDrag(event) {
    this.isDragging = true
    
    // Store the slider element
    this.currentSlider = event.currentTarget.closest('[data-document-pages-target="compareSlider"]')
    
    // Prevent default to avoid text selection during drag
    event.preventDefault()
  }
  
  handleDrag(event) {
    if (!this.isDragging || !this.currentSlider) return
    
    // Get slider dimensions
    const sliderRect = this.currentSlider.getBoundingClientRect()
    
    // Calculate position (mouse or touch)
    let clientX = 0
    if (event.type === 'touchmove') {
      clientX = event.touches[0].clientX
    } else {
      clientX = event.clientX
    }
    
    // Calculate percentage (constrained between 0-100%)
    let percentage = ((clientX - sliderRect.left) / sliderRect.width) * 100
    percentage = Math.max(0, Math.min(100, percentage))
    
    // Find the relevant slider handle and version container for this slider
    const handle = this.currentSlider.querySelector('[data-document-pages-target="sliderHandle"]')
    const versionContainer = this.currentSlider.querySelector('[data-document-pages-target="compareVersionContainer"]')
    const versionImage = versionContainer ? versionContainer.querySelector('img') : null
    const originalContainer = this.currentSlider.querySelector('.document-app__compare-original')
    const originalImage = originalContainer ? originalContainer.querySelector('img') : null
    
    if (handle && versionContainer) {
      // Update slider position
      handle.style.left = `${percentage}%`
      versionContainer.style.width = `${percentage}%`
      
      // Important: Ensure proper sizing for both images
      const containerWidth = this.currentSlider.offsetWidth
      
      // Maintain original image sizing
      if (originalImage) {
        originalImage.style.position = 'absolute'
        originalImage.style.top = '0'
        originalImage.style.left = '0'
        originalImage.style.width = '100%'
        originalImage.style.height = '100%'
        originalImage.style.objectFit = 'contain'
        originalImage.style.objectPosition = 'center'
      }
      
      // Ensure version image doesn't resize when slider moves
      if (versionImage) {
        versionImage.style.position = 'absolute'
        versionImage.style.top = '0'
        versionImage.style.left = '0'
        versionImage.style.width = `${containerWidth}px`
        versionImage.style.height = '100%'
        versionImage.style.maxWidth = 'none'
        versionImage.style.objectFit = 'contain'
        versionImage.style.objectPosition = 'center'
        versionImage.style.transform = 'none'
      }
    }
  }
  
  stopDrag() {
    this.isDragging = false
    this.currentSlider = null
  }

  updatePageDisplay() {
    // Handle page counter if it exists
    if (this.hasCurrentPageNumTarget) {
      this.currentPageNumTarget.textContent = this.currentPage + 1
    }
    
    // Check if this document has pagination controls (PDF) or is a single image
    const hasPagination = this.hasPrevButtonTarget && this.hasNextButtonTarget
    
    // Only update button states if pagination exists
    if (hasPagination) {
      this.prevButtonTarget.disabled = this.currentPage === 0 || this.totalPages <= 1
      this.nextButtonTarget.disabled = this.currentPage === this.totalPages - 1 || this.totalPages <= 1
    }
    
    // Check if we're in comparison mode
    const isCompareMode = this.hasCompareSwitchTarget && this.compareSwitchTarget.checked
    
    // Update regular view (if not in compare mode or if we need to keep it updated in the background)
    if (!isCompareMode || true) { // Always update regular view for proper state tracking
      // Update page positions with 3D effect
      this.pageTargets.forEach((page, index) => {
        const pageIndex = parseInt(page.dataset.pageIndex)
        
        // Calculate relative position to current page
        const relativePos = pageIndex - this.currentPage
        
        // Apply 3D transformations
        if (relativePos === 0) {
          // Current page - front and center
          page.style.transform = `translateZ(0px) translateY(0px) rotateY(0deg)`
          page.style.opacity = "1"
          page.style.zIndex = "100"
          page.classList.add("active")
        } else if (relativePos > 0) {
          // Pages after current - stack to the right
          page.style.transform = `translateZ(${-15 * relativePos}px) translateY(${7 * relativePos}px) rotateY(${-2 * relativePos}deg)`
          page.style.opacity = `${Math.max(0.6, 1 - (0.15 * relativePos))}`
          page.style.zIndex = `${100 - relativePos}`
          page.classList.remove("active")
        } else {
          // Pages before current - stack to the left
          const absPos = Math.abs(relativePos)
          page.style.transform = `translateZ(${-15 * absPos}px) translateY(${7 * absPos}px) rotateY(${2 * absPos}deg)`
          page.style.opacity = `${Math.max(0.6, 1 - (0.15 * absPos))}`
          page.style.zIndex = `${100 - absPos}`
          page.classList.remove("active")
        }
      })
    }
    
    // Update comparison pages if in compare mode
    if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
      this.updateComparisonPages()
    }
    
    // Update indicators if they exist
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, index) => {
        if (index === this.currentPage) {
          indicator.style.width = "30px"
          indicator.style.backgroundColor = "#4a87ff"
        } else {
          indicator.style.width = "8px"
          indicator.style.backgroundColor = "#d9e1ee"
        }
      })
    }
  }
}